import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import {
  MdAdd,
  MdShoppingBag,
  MdArrowBack,
  MdArrowForward,
  MdCheckroom,
  MdSync,
} from "react-icons/md";
import "./ProductListing.css";
import { useProductListing } from "../../customHook/ProductListing/useProductListing";
import ProductCard from "../../components/ProductCard/ProductCard";
import SearchBar from "../../components/SearchBar/SearchBar";
import FilterOptions from "../../components/FilterOptions/FilterOptions";
import ProductActionsMenu from "../../components/ProductActionsMenu/ProductActionsMenu";
import ProductUploadModal from "../../components/ProductUploadModal/ProductUploadModal";
import BulkProductUploadModal from "../../components/BulkProductUploadModal/BulkProductUploadModal";
import BulkProductImagesUploadModal from "../../components/BulkProductImagesUploadModal/BulkProductImagesUploadModal";
import InternalPagination from "../../components/InternalPagination/InternalPagination";
import ZoomModal from "../../components/ZoomModal/ZoomModal";

const COLLECTION_PALETTES = [
  { badgeClass: "badge-indigo", barColor: "bg-[#6366F1]" },
  { badgeClass: "badge-emerald", barColor: "bg-[#10B981]" },
  { badgeClass: "badge-amber", barColor: "bg-[#F59E0B]" },
  { badgeClass: "badge-rose", barColor: "bg-[#F43F5E]" },
  { badgeClass: "badge-sky", barColor: "bg-[#0EA5E9]" },
  { badgeClass: "badge-violet", barColor: "bg-[#8B5CF6]" },
  { badgeClass: "badge-teal", barColor: "bg-[#14B8A6]" },
  { badgeClass: "badge-orange", barColor: "bg-[#F97316]" },
];

// Helper to determine collection badge styling by title or rotating index
const getCollectionTheme = (title = "", index = 0) => {
  const t = title.toLowerCase();
  if (t.includes("trad") || t.includes("ethnic") || t.includes("wedding")) {
    return { badgeClass: "badge-amber", barColor: "bg-[#F59E0B]" };
  }
  if (t.includes("trend") || t.includes("fashion") || t.includes("western")) {
    return { badgeClass: "badge-indigo", barColor: "bg-[#6366F1]" };
  }
  if (t.includes("top") || t.includes("shirt") || t.includes("formal")) {
    return { badgeClass: "badge-sky", barColor: "bg-[#0EA5E9]" };
  }
  if (t.includes("bottom") || t.includes("pant") || t.includes("denim")) {
    return { badgeClass: "badge-emerald", barColor: "bg-[#10B981]" };
  }
  return COLLECTION_PALETTES[Math.abs(index) % COLLECTION_PALETTES.length];
};

const CollectionPanel = ({
  title,
  products = [],
  index = 0,
  userRole,
  handleToggleProduct,
  handleEditModalOpen,
  openZoomModal,
  onAddProduct,
}) => {
  const railRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const theme = useMemo(() => getCollectionTheme(title, index), [title, index]);

  const updateScrollState = useCallback(() => {
    if (railRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = railRef.current;
      setScrollState({
        canScrollLeft: scrollLeft > 2,
        canScrollRight: scrollLeft + clientWidth < scrollWidth - 2,
      });
    }
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return;

    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    const timer = setTimeout(updateScrollState, 150);

    return () => {
      clearTimeout(timer);
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [products, updateScrollState]);

  const scrollRail = (direction) => {
    if (railRef.current) {
      const scrollAmount = railRef.current.clientWidth * 0.8;
      railRef.current.scrollBy({
        left: direction * scrollAmount,
        behavior: "smooth",
      });
    }
  };

  if (!products || products.length === 0) return null;

  const isAdmin = userRole === "admin" || userRole === "superadmin";

  return (
    <div className="collection-panel p-3.5 sm:p-5 mb-4 sm:mb-5">
      {/* Collection Header Bar */}
      <div className="flex justify-between items-center mb-3 sm:mb-3.5 pb-2 sm:pb-2.5 border-b border-slate-100 gap-2">
        <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
          <span className={`w-1.5 h-5 sm:h-6 rounded-full shrink-0 ${theme.barColor}`} />
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-1.5 sm:gap-2 min-w-0">
            <h2 className="font-headline-md text-[15px] sm:text-[17px] text-slate-900 font-bold tracking-tight m-0 truncate">
              {title}
            </h2>
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 sm:py-1 rounded-full text-[11px] sm:text-xs font-semibold shrink-0 ${theme.badgeClass}`}>
              <MdCheckroom size={13} className="shrink-0" />
              <span>{products.length} {products.length === 1 ? "look" : "looks"}</span>
            </span>
          </div>
        </div>

        {/* Scroll Navigation Arrows */}
        {products.length > 1 && (
          <div className="flex gap-1.5 shrink-0">
            <button
              onClick={() => scrollRail(-1)}
              disabled={!scrollState.canScrollLeft}
              className={`w-7 h-7 rounded-full border flex items-center justify-center transition-all shadow-2xs focus:outline-none ${
                scrollState.canScrollLeft
                  ? "text-slate-700 border-slate-300 hover:text-slate-900 hover:border-slate-400 active:scale-95 cursor-pointer bg-white"
                  : "text-slate-300 bg-slate-50 border-slate-200/60 cursor-not-allowed"
              }`}
              type="button"
              aria-label="Scroll left"
            >
              <MdArrowBack size={15} />
            </button>
            <button
              onClick={() => scrollRail(1)}
              disabled={!scrollState.canScrollRight}
              className={`w-7 h-7 rounded-full border flex items-center justify-center transition-all shadow-2xs focus:outline-none ${
                scrollState.canScrollRight
                  ? "text-slate-700 border-slate-300 hover:text-slate-900 hover:border-slate-400 active:scale-95 cursor-pointer bg-white"
                  : "text-slate-300 bg-slate-50 border-slate-200/60 cursor-not-allowed"
              }`}
              type="button"
              aria-label="Scroll right"
            >
              <MdArrowForward size={15} />
            </button>
          </div>
        )}
      </div>

      {/* Horizontal Scroll Rail */}
      <div
        ref={railRef}
        className="flex gap-4 overflow-x-auto rail-container pt-1 pb-1"
      >
        {products.map((item, i) => (
          <ProductCard
            key={item.id || item.iProductId || `${title}-${i}`}
            title={item.title}
            sku={
              item.sku ||
              item.vSku ||
              item.vSKU ||
              item.skuId ||
              item.iProductId ||
              item.id
            }
            price={
              item.price ??
              item.decPrice ??
              item.decPriceUsd ??
              item.decPriceInr
            }
            images={item.images}
            generationCount={item.generationCount || 0}
            bIsActive={item.bIsActive ?? item.isActive ?? true}
            canToggle={isAdmin}
            onToggle={() => handleToggleProduct(item)}
            canEdit={isAdmin}
            onEdit={() => handleEditModalOpen(item)}
            onZoom={openZoomModal}
          />
        ))}
      </div>
    </div>
  );
};

const ProductListing = () => {
  const {
    userRole,
    category,
    onCategoryChange,
    modelType,
    mappedModelType,
    onModelTypeChange,
    searchTerm,
    onSearchChange,
    onFilterChange,
    apiFilters,
    collections,
    totalProductsCount,
    loading,
    error,
    refreshProducts,
    pagination,
    onPageChange,
    handleToggleProduct,
    // Modals
    isUploadModalOpen,
    editingProduct,
    isBulkUploadModalOpen,
    isBulkImageUploadModalOpen,
    isProductUploadLoading,
    handleUploadModalOpen,
    handleEditModalOpen,
    handleUploadModalClose,
    handleBulkUploadModalOpen,
    handleBulkUploadModalClose,
    handleBulkImageUploadModalOpen,
    handleBulkImageUploadModalClose,
    handleUploadSubmit,
    fetchFilterCategories,
    fetchCollectionCategories,
    // Zoom
    zoomModal,
    openZoomModal,
    closeZoomModal,
  } = useProductListing();

  const isAdmin = userRole === "admin" || userRole === "superadmin";

  const hasAnyProducts = collections.some(
    (col) => Array.isArray(col.products) && col.products.length > 0
  );

  return (
    <div className="app-page product-listing-screen flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Scrollable Content Body */}
      <div className="flex-1 overflow-y-auto min-h-0 pb-16">
        {/* Page Header (Scrolls away, not sticky) */}
        <div className="product-selector-header px-4 sm:px-6 pt-3 sm:pt-4 pb-2 sm:pb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 sm:gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="font-headline-md text-lg sm:text-[20px] text-primary font-bold tracking-tight m-0">
                Products
              </h1>
              <span className="font-body-md inline-flex items-center gap-1.5 text-primary bg-primary/[0.04] border border-primary/10 px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-bold shadow-[0_1px_2px_rgba(0,0,0,0.02)] whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-primary/40" />
                {totalProductsCount} {totalProductsCount === 1 ? "Product" : "Products"}
              </span>
            </div>
            <p className="font-body-md text-[10px] sm:text-xs text-secondary mt-0.5">
              Manage your garment catalog, SKUs, and pricing collection-wise.
            </p>
          </div>

          {/* Header Action Buttons (Desktop only) */}
          {isAdmin && (
            <div className="hidden sm:flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleUploadModalOpen}
                className="app-button-secondary flex items-center justify-center gap-1.5 px-3 h-8 rounded-md text-xs font-semibold bg-white border border-slate-300 hover:border-slate-400 text-slate-700 shadow-sm focus:outline-none cursor-pointer whitespace-nowrap shrink-0 transition-all"
                title="Upload Single Product"
              >
                <MdAdd size={16} />
                <span>Add Product</span>
              </button>
              <ProductActionsMenu
                onBulkProductUpload={handleBulkUploadModalOpen}
                onBulkImageUpload={handleBulkImageUploadModalOpen}
              />
            </div>
          )}
        </div>

        {/* Primary Controls Bar (Sticky at top when scrolling) */}
        <div className="product-controls-wrapper sticky top-0 z-30 px-4 sm:px-6 py-2 bg-[#F1F4F9]/95 backdrop-blur-sm">
          <div className="app-control-bar product-controls-shell flex flex-col lg:flex-row lg:items-center justify-between gap-2.5 sm:gap-4 p-2.5 sm:p-3">
            <div className="product-primary-controls flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 sm:gap-4 w-full lg:w-auto">
              {/* Generation Type selector tabs */}
              <div className="generation-type-control flex items-center gap-1.5 sm:gap-3 shrink-0">
                <span className="hidden lg:inline-block text-[10px] text-secondary uppercase tracking-wider font-bold">
                  Generation Type : 
                </span>
                <div className="generation-type-tabs inline-flex bg-slate-100/80 p-0.5 rounded-md gap-0.5">
                  <button
                    onClick={() => onModelTypeChange("mannequin")}
                    className={`px-2 sm:px-3.5 py-1 rounded font-semibold text-[11px] sm:text-xs transition-all duration-200 focus:outline-none ${
                      modelType === "mannequin"
                        ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                        : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                    }`}
                    type="button"
                  >
                    Mannequin
                  </button>
                  <button
                    onClick={() => onModelTypeChange("model")}
                    className={`px-2 sm:px-3.5 py-1 rounded font-semibold text-[11px] sm:text-xs transition-all duration-200 focus:outline-none ${
                      modelType === "model"
                        ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                        : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                    }`}
                    type="button"
                  >
                    Model
                  </button>
                </div>
              </div>

              {/* Gender selector tabs */}
              <div className="gender-tabs flex gap-0.5 bg-slate-100/80 p-0.5 rounded-md shrink-0">
                <button
                  onClick={() => onCategoryChange("men")}
                  className={`px-2.5 sm:px-4 py-1 rounded font-semibold text-[11px] sm:text-xs transition-all duration-200 ${
                    category === "men"
                      ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                      : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                  } focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:outline-none active:ring-0`}
                  type="button"
                >
                  Men
                </button>
                <button
                  onClick={() => onCategoryChange("women")}
                  className={`px-2.5 sm:px-4 py-1 rounded font-semibold text-[11px] sm:text-xs transition-all duration-200 ${
                    category === "women"
                      ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                      : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                  } focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:outline-none active:ring-0`}
                  type="button"
                >
                  Women
                </button>
              </div>
            </div>

            {/* Search & Filters */}
            <div className="product-search-actions flex items-center gap-1.5 sm:gap-2 lg:justify-end relative min-w-0 w-full lg:w-auto">
              <SearchBar
                value={searchTerm}
                onChange={onSearchChange}
                placeholder="Search products..."
                className="flex-1 min-w-0 w-full lg:w-56"
              />
              <FilterOptions
                filters={apiFilters}
                gender={category}
                modelType={modelType}
                onFilterChange={onFilterChange}
                className="shrink-0"
              />
              {isAdmin && (
                <div className="flex sm:hidden items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={handleUploadModalOpen}
                    className="app-button-secondary flex items-center justify-center gap-1 px-2.5 h-8 rounded-md text-xs font-semibold bg-white border border-slate-300 hover:border-slate-400 text-slate-700 shadow-sm focus:outline-none cursor-pointer whitespace-nowrap shrink-0 transition-all"
                    title="Upload Single Product"
                  >
                    <MdAdd size={16} />
                    <span>Add</span>
                  </button>
                  <ProductActionsMenu
                    onBulkProductUpload={handleBulkUploadModalOpen}
                    onBulkImageUpload={handleBulkImageUploadModalOpen}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Collection-wise Product Sections (Enclosed Panels) */}
        <div className="product-sections px-4 sm:px-6 pt-1 pb-2">
          {loading ? (
            <div className="flex-1 flex flex-col items-center justify-center py-20 w-full text-center">
              <MdSync className="text-[36px] text-slate-400 animate-spin mb-2" />
              <p className="text-sm font-semibold text-secondary">Loading studio catalog...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center bg-red-50 border border-red-200 rounded-xl p-6">
              <p className="text-sm font-semibold text-red-600 mb-2">{error}</p>
              <button
                type="button"
                onClick={refreshProducts}
                className="px-3 py-1.5 bg-white border border-red-300 rounded-lg text-xs font-semibold text-red-700 shadow-sm hover:bg-red-50"
              >
                Retry
              </button>
            </div>
          ) : !hasAnyProducts ? (
            <div className="app-empty-state flex flex-col items-center justify-center py-20 w-full gap-2 bg-white rounded-2xl border border-slate-200/80 p-8 shadow-2xs">
              <MdShoppingBag className="text-[48px] text-slate-300 mb-2" />
              <p className="text-sm font-semibold text-slate-700">No products found matching your filters</p>
            </div>
          ) : (
            collections.map((col, idx) => (
              <CollectionPanel
                key={col.id || col.title || `collection-${idx}`}
                title={col.title}
                products={col.products}
                index={idx}
                userRole={userRole}
                handleToggleProduct={handleToggleProduct}
                handleEditModalOpen={handleEditModalOpen}
                openZoomModal={openZoomModal}
                onAddProduct={handleUploadModalOpen}
              />
            ))
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="product-pagination-container px-4 sm:px-6 pb-16 shrink-0 overflow-x-auto">
            <InternalPagination
              page={pagination.page}
              limit={pagination.limit}
              total={pagination.total}
              totalPages={pagination.totalPages}
              onPageChange={onPageChange}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      <ProductUploadModal
        isOpen={isUploadModalOpen}
        onClose={handleUploadModalClose}
        onSubmit={handleUploadSubmit}
        isLoading={isProductUploadLoading}
        getFilterCategories={fetchFilterCategories}
        getCollectionCategories={fetchCollectionCategories}
        defaultGender={category}
        defaultMode={mappedModelType}
        productToEdit={editingProduct}
      />

      <BulkProductUploadModal
        isOpen={isBulkUploadModalOpen}
        onClose={handleBulkUploadModalClose}
        onRefreshProducts={refreshProducts}
      />

      <BulkProductImagesUploadModal
        isOpen={isBulkImageUploadModalOpen}
        onClose={handleBulkImageUploadModalClose}
        onRefreshProducts={refreshProducts}
      />

      {/* Image Zoom Modal */}
      <ZoomModal
        modalId="productZoomModal"
        front={zoomModal.front}
        back={zoomModal.back}
        onClose={closeZoomModal}
      />
    </div>
  );
};

export default ProductListing;
